import { Package, History, Plus, X, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { formatGiacenza } from "../../utils/udmUtils";

export const IngredientiFormDialog = ({
  open, onOpenChange, editItem, form, setForm, onSubmit, saving,
  categorie, fornitori
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-card border-border">
      <DialogHeader>
        <DialogTitle className="text-foreground flex items-center gap-2">
          <Package className="h-5 w-5 text-primary" />
          {editItem ? "Modifica Materia Prima" : "Nuova Materia Prima"}
        </DialogTitle>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label className="text-foreground/70">Nome *</Label>
          <Input
            value={form.nome}
            onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
            placeholder="es. Mozzarella Fior di Latte"
            className="bg-card border-border text-foreground"
            data-testid="input-nome"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label className="text-foreground/70">Categoria</Label>
            <Select value={form.categoria_id} onValueChange={(v) => setForm(prev => ({ ...prev, categoria_id: v }))}>
              <SelectTrigger className="bg-card border-border text-foreground">
                <SelectValue placeholder="Seleziona..." />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {categorie.map(c => (
                  <SelectItem key={c.id} value={c.id} className="text-foreground">{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-foreground/70">Unità Misura</Label>
            <Select value={form.unita_misura} onValueChange={(v) => setForm(prev => ({ ...prev, unita_misura: v }))}>
              <SelectTrigger className="bg-card border-border text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                <SelectItem value="kg" className="text-foreground">kg — chilogrammi</SelectItem>
                <SelectItem value="g" className="text-foreground">g — grammi</SelectItem>
                <SelectItem value="litri" className="text-foreground">L — litri</SelectItem>
                <SelectItem value="ml" className="text-foreground">ml — millilitri</SelectItem>
                <SelectItem value="pz" className="text-foreground">pz — pezzi</SelectItem>
                <SelectItem value="fetta" className="text-foreground">fetta</SelectItem>
                <SelectItem value="bustina" className="text-foreground">bustina</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!editItem && (
          <div className="space-y-2 p-3 bg-lime-400/5 rounded-xl border border-lime-400/20">
            <Label className="text-lime-400 text-sm font-medium">Prezzo Iniziale (opzionale)</Label>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Prezzo €/{form.unita_misura}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.prezzo_kg}
                  onChange={(e) => setForm(prev => ({ ...prev, prezzo_kg: e.target.value }))}
                  placeholder="0.00"
                  className="bg-card border-border text-foreground"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Fornitore</Label>
                <Select value={form.fornitore_id} onValueChange={(v) => setForm(prev => ({ ...prev, fornitore_id: v }))}>
                  <SelectTrigger className="bg-card border-border text-foreground">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {fornitori.map(f => (
                      <SelectItem key={f.id} value={f.id} className="text-foreground">{f.ragione_sociale}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-foreground/70">Note</Label>
          <Input
            value={form.note}
            onChange={(e) => setForm(prev => ({ ...prev, note: e.target.value }))}
            placeholder="Note aggiuntive..."
            className="bg-card border-border text-foreground"
          />
        </div>

        <DialogFooter className="gap-2 pt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1 border-border text-foreground hover:bg-muted">
            Annulla
          </Button>
          <Button type="submit" disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold">
            {saving ? "Salvataggio..." : (editItem ? "Salva" : "Crea")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

export const IngredientiDetailDialog = ({
  open, onOpenChange, selectedItem, localeCorrente, fornitori,
  showAddPrezzo, setShowAddPrezzo, prezzoForm, setPrezzoForm,
  onAddPrezzo, savingPrezzo
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl bg-card border-border">
      <DialogHeader>
        <DialogTitle className="text-foreground flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          Storico Prezzi - {selectedItem?.nome}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div className="p-3 bg-muted/50 rounded-xl">
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">Unità di misura:</span>
            <span className="text-foreground font-medium">{selectedItem?.prezziDetail?.unita_misura}</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-muted-foreground">Quantità in magazzino:</span>
            <span className="text-foreground font-medium">{formatGiacenza(selectedItem?.quantita_disponibile || 0, selectedItem?.unita_misura)}</span>
          </div>
        </div>

        {showAddPrezzo ? (
          <div className="p-4 bg-primary/5 rounded-xl border border-primary/20 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-foreground font-medium flex items-center gap-2">
                <Plus className="h-4 w-4 text-primary" />
                Aggiungi Prezzo Fornitore
              </h4>
              <Button variant="ghost" size="icon" onClick={() => setShowAddPrezzo(false)} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Fornitore *</Label>
                <Select
                  value={prezzoForm.fornitore_id}
                  onValueChange={(v) => setPrezzoForm(prev => ({ ...prev, fornitore_id: v }))}
                >
                  <SelectTrigger className="bg-card border-border text-foreground">
                    <SelectValue placeholder="Seleziona..." />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {fornitori.map(f => (
                      <SelectItem key={f.id} value={f.id} className="text-foreground">
                        {f.ragione_sociale}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-muted-foreground text-xs">Prezzo €/{selectedItem?.unita_misura} *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={prezzoForm.prezzo_kg}
                  onChange={(e) => setPrezzoForm(prev => ({ ...prev, prezzo_kg: e.target.value }))}
                  placeholder="0.00"
                  className="bg-card border-border text-foreground"
                />
              </div>
            </div>
            <Button
              onClick={onAddPrezzo}
              disabled={savingPrezzo || !prezzoForm.fornitore_id || !prezzoForm.prezzo_kg}
              className="w-full bg-primary hover:bg-primary/90 text-white"
            >
              {savingPrezzo ? "Salvataggio..." : "Salva Prezzo"}
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddPrezzo(true)}
            className="w-full py-3 px-4 rounded-xl font-medium flex items-center justify-center gap-2"
            style={{ backgroundColor: '#6C63FF', color: '#FFFFFF' }}
            data-testid="btn-add-prezzo"
          >
            <Plus className="h-4 w-4" />
            Aggiungi Prezzo Fornitore
          </button>
        )}

        <div className="space-y-3">
          <h3 className="text-foreground/70 font-medium text-sm">Prezzi per Fornitore</h3>
          <p className="text-xs text-muted-foreground">
            I prezzi mostrati sono specifici per il locale <span className="text-primary font-medium">{localeCorrente?.nome}</span>
          </p>

          {selectedItem?.prezziDetail?.fornitori?.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">Nessun prezzo registrato</p>
          ) : (
            selectedItem?.prezziDetail?.fornitori?.map((fornitore, idx) => (
              <div key={`${fornitore.fornitore_id}-${fornitore.locale_id || 'global'}-${idx}`} className="bg-muted/50 rounded-xl p-3 border border-border">
                <div className="flex justify-between items-center mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-medium">{fornitore.fornitore_nome}</span>
                    {fornitore.is_global ? (
                      <span className="text-xs px-2 py-0.5 bg-blue-500/20 rounded-full text-blue-400">Globale</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-green-500/20 rounded-full text-green-400">Questo locale</span>
                    )}
                  </div>
                  <span className="text-primary font-bold text-lg">
                    €{fornitore.prezzo_attuale.toFixed(2)}/{selectedItem?.prezziDetail?.unita_misura}
                  </span>
                </div>

                {fornitore.storico.length > 1 && (
                  <div className="mt-2 pt-2 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Storico prezzi:</p>
                    <div className="space-y-1">
                      {fornitore.storico.map((s, sIdx) => {
                        const prevPrezzo = sIdx < fornitore.storico.length - 1 ? fornitore.storico[sIdx + 1].prezzo_kg : null;
                        const variazione = prevPrezzo ? ((s.prezzo_kg - prevPrezzo) / prevPrezzo * 100) : null;

                        return (
                          <div key={sIdx} className="flex justify-between text-xs">
                            <span className="text-muted-foreground">
                              {new Date(s.data).toLocaleDateString('it-IT')}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-foreground">€{s.prezzo_kg.toFixed(2)}</span>
                              {variazione !== null && (
                                <span className={`flex items-center ${variazione > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                  {variazione > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                                  {Math.abs(variazione).toFixed(1)}%
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      <DialogFooter className="pt-4">
        <Button onClick={() => onOpenChange(false)} className="w-full bg-muted hover:bg-muted/80 text-foreground">
          Chiudi
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
